// Application Detail page component for InfluenceHub MVP
// Shows detailed information about an influencer application for brand review
// Layout: Campaign Snapshot (top) + Influencer Profile (left) + Status (right)

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebaseConfig.js';
import Navbar from '../../components/layout/Navbar';
import Sidebar from '../../components/layout/Sidebar';
import { formatCompact, formatPct } from '../../utils/num.js';
import { 
  APPLICATION_STATUSES, 
  BRAND_UPDATEABLE_STATUSES,
  normalizeApplicationStatus,
  getStatusBadgeConfig 
} from '../lib/applicationStatus.js';

const STATUS_OPTIONS = [
  { value: APPLICATION_STATUSES.PENDING_REVIEW, label: 'Pending Review' },
  { value: APPLICATION_STATUSES.ACCEPTED, label: 'Accepted' },
  { value: APPLICATION_STATUSES.REJECTED, label: 'Rejected' },
];

const ApplicationDetail = ({ applicationId, onNavigate }) => {
  const { user, profile } = useAuth();
  const [application, setApplication] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState(APPLICATION_STATUSES.PENDING_REVIEW);
  const [avatarError, setAvatarError] = useState(false);

  // Get initials helper
  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return '—';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const normalized = normalizeApplicationStatus(status);
    const config = getStatusBadgeConfig(normalized);
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  // Helper to detect platform from URL
  const getPlatformFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('instagram.com')) return 'instagram';
    if (lowerUrl.includes('tiktok.com')) return 'tiktok';
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
    if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return 'x';
    return null;
  };

  // Parse location from shipping address
  const parseLocation = (shipping) => {
    if (!shipping) return null;
    const parts = [shipping.city, shipping.state, shipping.country].filter(part => part && part.trim());
    return parts.length > 0 ? parts.join(', ') : null;
  };

  // Fetch application, campaign, and influencer data
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('🔄 Fetching application details for:', applicationId);
        
        if (!user || !profile) {
          setError('Please sign in to view application details');
          setLoading(false);
          return;
        }

        if (profile.userType !== 'brand') {
          setError('Only brands can view application details');
          setLoading(false);
          return;
        }

        if (!db) {
          throw new Error('Firebase database not initialized');
        }

        // Fetch application
        const applicationRef = doc(db, 'applications', applicationId);
        const applicationSnap = await getDoc(applicationRef);
        
        if (!applicationSnap.exists()) {
          setError('Application not found');
          setLoading(false);
          return;
        }

        const appData = {
          id: applicationSnap.id,
          ...applicationSnap.data(),
          submittedAt: applicationSnap.data().submittedAt,
          createdAt: applicationSnap.data().createdAt?.toDate() || new Date(),
          updatedAt: applicationSnap.data().updatedAt?.toDate() || new Date()
        };

        // Debug: Log application data for troubleshooting
        console.log('📋 Application data:', {
          id: appData.id,
          brandId: appData.brandId,
          brandUid: appData.brandUid,
          campaignId: appData.campaignId,
          influencerId: appData.influencerId,
          currentUserUid: user.uid,
          status: appData.status
        });

        // Security check: ensure application belongs to this brand
        const isBrandOwner = appData.brandId === user.uid || appData.brandUid === user.uid;
        if (!isBrandOwner) {
          console.warn('⚠️ Access denied - brandId mismatch:', {
            appBrandId: appData.brandId,
            appBrandUid: appData.brandUid,
            userUid: user.uid
          });
          setError('Access denied. This application does not belong to your brand.');
          setLoading(false);
          return;
        }

        setApplication(appData);
        setStatus(normalizeApplicationStatus(appData.status || APPLICATION_STATUSES.PENDING_REVIEW));

        // Fetch campaign
        if (appData.campaignId) {
          try {
            const campaignRef = doc(db, 'campaigns', appData.campaignId);
            const campaignSnap = await getDoc(campaignRef);
            if (campaignSnap.exists()) {
              const campaignData = {
                id: campaignSnap.id,
                ...campaignSnap.data(),
                deadline: campaignSnap.data().deadline,
                createdAt: campaignSnap.data().createdAt?.toDate() || new Date()
              };
              setCampaign(campaignData);
            }
          } catch (err) {
            console.warn('Could not fetch campaign:', err);
          }
        }

        // Fetch influencer profile
        if (appData.influencerId) {
          try {
            const influencerRef = doc(db, 'users', appData.influencerId);
            const influencerSnap = await getDoc(influencerRef);
            if (influencerSnap.exists()) {
              let influencerData = {
                id: influencerSnap.id,
                ...influencerSnap.data()
              };
              
              // Check if profile picture exists in Firestore
              const existingProfilePicUrl = 
                influencerData?.profile?.media?.profilePictureUrl ||
                influencerData?.profilePictureUrl ||
                influencerData?.avatarUrl ||
                null;
              
              // If no profile picture URL in Firestore, try to fetch from Storage (non-blocking)
              if (!existingProfilePicUrl && appData.influencerId && storage) {
                // Run this async without blocking the page load
                (async () => {
                  try {
                    console.log('🔍 No profile picture URL in Firestore, checking Firebase Storage...');
                    const storageRef = ref(storage, `uploads/${appData.influencerId}/profile-picture`);
                    const downloadURL = await getDownloadURL(storageRef);
                    console.log('✅ Found profile picture in Storage:', downloadURL);
                    
                    // Update Firestore with the URL for future use
                    const currentProfile = influencerData.profile || {};
                    const currentMedia = currentProfile.media || {};
                    
                    await updateDoc(influencerRef, {
                      profile: {
                        ...currentProfile,
                        media: {
                          ...currentMedia,
                          profilePictureUrl: downloadURL
                        }
                      }
                    });
                    
                    console.log('✅ Updated Firestore with profile picture URL');
                    
                    // Update local state to show the image
                    setInfluencer(prev => ({
                      ...prev,
                      profile: {
                        ...(prev?.profile || {}),
                        media: {
                          ...(prev?.profile?.media || {}),
                          profilePictureUrl: downloadURL
                        }
                      }
                    }));
                  } catch (storageError) {
                    // File doesn't exist in Storage or CORS error - handle gracefully
                    const errorCode = storageError?.code || storageError?.message || '';
                    const errorMessage = String(storageError?.message || storageError || '');
                    
                    // Silently handle CORS errors - they're expected if CORS isn't configured yet
                    // After CORS is configured, this will work automatically
                    if (errorMessage.includes('CORS') || errorMessage.includes('cors') || 
                        errorCode.includes('storage/unauthorized') || 
                        errorCode.includes('storage/unknown') ||
                        errorMessage.includes('blocked by CORS')) {
                      // CORS error - don't log, just continue silently
                      // Once CORS is configured, this will work on next page load
                      return;
                    }
                    
                    if (errorCode.includes('object-not-found') || errorCode.includes('not-found') ||
                        errorCode === 'storage/object-not-found') {
                      console.log('ℹ️ Profile picture not found in Storage');
                    } else {
                      // Only log non-CORS errors
                      console.warn('⚠️ Error checking Storage (non-critical):', storageError?.message || storageError);
                    }
                    // Don't crash - just continue without the profile picture
                  }
                })();
              }
              
              setInfluencer(influencerData);
            }
          } catch (err) {
            console.warn('Could not fetch influencer profile:', err);
          }
        }

        console.log('✅ Application data loaded');
      } catch (error) {
        console.error('❌ Error fetching application details:', error);
        setError(`Failed to load application: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (applicationId) {
      fetchData();
    }
  }, [applicationId, user, profile]);

  // Update application status
  const handleUpdateStatus = async () => {
    if (!application || updating) return;

    // Validate status
    const normalizedStatus = normalizeApplicationStatus(status);
    if (!BRAND_UPDATEABLE_STATUSES.includes(normalizedStatus) && normalizedStatus !== APPLICATION_STATUSES.PENDING_REVIEW) {
      alert('Invalid status. Only "Accepted" or "Rejected" can be set.');
      return;
    }

    try {
      setUpdating(true);
      console.log(`🔄 Updating application ${application.id} to ${normalizedStatus}...`);
      console.log('📋 Application data before update:', {
        id: application.id,
        brandId: application.brandId,
        brandUid: application.brandUid,
        campaignId: application.campaignId,
        currentUserUid: user?.uid
      });

      const payload = {
        status: normalizedStatus,
        updatedAt: serverTimestamp()
      };

      // Update root application document (primary update)
      const applicationRef = doc(db, 'applications', application.id);
      console.log('📝 Attempting to update application document:', applicationRef.path);
      await updateDoc(applicationRef, payload);
      console.log('✅ Root application document updated successfully');

      // Update mirror document in subcollection (optional, don't fail if this errors)
      if (application.campaignId && application.influencerId) {
        try {
          const subRef = doc(db, 'campaigns', application.campaignId, 'applications', application.influencerId);
          await updateDoc(subRef, payload);
          console.log('✅ Subcollection mirror document updated successfully');
        } catch (subError) {
          // Log but don't fail - the main update succeeded
          console.warn('⚠️ Could not update subcollection mirror (non-critical):', subError);
        }
      }

      // Update local state
      setApplication(prev => ({ ...prev, status: normalizedStatus }));
      setStatus(normalizedStatus);

      const config = getStatusBadgeConfig(normalizedStatus);
      console.log('✅ Status updated successfully to:', normalizedStatus);
      alert(`Application status updated to ${config.label}`);
    } catch (error) {
      console.error('❌ Error updating status:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      alert(`Failed to update status: ${error.message}`);
      // Don't update local state if the update failed
    } finally {
      setUpdating(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-6xl mx-auto">
              <div className="animate-pulse space-y-6">
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                <div className="h-64 bg-gray-200 rounded-xl"></div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-6">
                    <div className="h-48 bg-gray-200 rounded-xl"></div>
                    <div className="h-48 bg-gray-200 rounded-xl"></div>
                  </div>
                  <div className="h-64 bg-gray-200 rounded-xl"></div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center py-12">
                <div className="text-red-600 text-6xl mb-4">⚠️</div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Application</h1>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={() => onNavigate('brand-applications')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Back to Applications
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📋</div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Not Found</h1>
                <p className="text-gray-600 mb-6">The requested application could not be found.</p>
                <button
                  onClick={() => onNavigate('brand-applications')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Back to Applications
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Extract influencer data
  const influencerName = influencer?.firstName && influencer?.lastName
    ? `${influencer.firstName} ${influencer.lastName}`.trim()
    : influencer?.displayName || application.influencerName || 'Unknown Influencer';
  
  const influencerEmail = influencer?.email || application.influencerEmail || null;
  const influencerProfile = influencer?.profile || {};
  
  // Try ALL possible paths for profile picture (comprehensive check)
  // Check in order of most likely to least likely
  const influencerAvatar = 
    influencerProfile?.media?.profilePictureUrl ||           // Primary path from signup
    influencer?.profile?.media?.profilePictureUrl ||         // Direct nested check
    influencerProfile?.account?.profilePictureUrl ||         // Alternative account path
    influencer?.profile?.account?.profilePictureUrl ||        // Direct account path
    influencer?.profilePictureUrl ||                         // Top-level
    influencer?.avatarUrl ||                                 // Alternative field name
    influencer?.photoURL ||                                  // Firebase Auth photoURL
    influencer?.profileImageUrl ||                           // TypeScript interface field
    null;
  
  const influencerLocation = parseLocation(influencerProfile?.account?.contact?.shipping) || influencer?.location || null;
  const influencerBio = influencerProfile?.bio?.text || influencer?.bio || null;
  const influencerNiches = influencerProfile?.audience?.niches || influencer?.categories || influencer?.niches || [];
  const totalFollowers = influencerProfile?.audience?.totalFollowers || influencer?.followers || influencer?.totalFollowers || null;
  const engagementRate = influencerProfile?.audience?.engagementRate || influencer?.engagementRate || null;
  const socialLinks = influencerProfile?.socialLinks || influencer?.socialLinks || {};
  
  // Get social links with platforms
  const socialLinksWithPlatforms = Object.entries(socialLinks)
    .map(([key, url]) => ({
      key,
      url,
      platform: getPlatformFromUrl(url)
    }))
    .filter(link => link.platform && link.url && link.url.trim());

  const campaignTitle = campaign?.title || application.campaignTitle || campaign?.campaignName || campaign?.name || 'Unknown Campaign';
  const brandName = profile?.companyName || profile?.displayName || profile?.name || 'Brand';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onNavigate={onNavigate} />
      <div className="flex">
        <Sidebar onNavigate={onNavigate} />
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Breadcrumbs */}
            <nav className="text-sm text-gray-500">
              <button
                onClick={() => onNavigate('dashboard')}
                className="hover:underline"
              >
                Dashboard
              </button>
              <span> / </span>
              <button
                onClick={() => onNavigate('brand-applications')}
                className="hover:underline"
              >
                Applications
              </button>
              <span> / </span>
              <span className="text-gray-900">{campaignTitle}</span>
              <span> / </span>
              <span className="text-gray-900">{influencerName}</span>
            </nav>

            {/* Campaign Snapshot */}
            <section className="bg-white border rounded-xl p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{campaignTitle}</h1>
                  <p className="text-xs text-gray-500 mt-1">Submitted {formatDate(application.submittedAt || application.createdAt)}</p>
                </div>
                {campaign?.hasPhysicalGift && (
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">🎁 Physical Gift</span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm mb-4">
                <div>
                  <div className="text-gray-500 mb-1">Collaboration Type</div>
                  <div className="font-medium text-gray-900">
                    {campaign?.collaborationType || application?.snapshot?.collaborationType || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Budget</div>
                  <div className="font-medium text-emerald-700">
                    {formatCurrency(campaign?.budgetUSD || application?.snapshot?.budgetUSD || campaign?.budget)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Content Submission Deadline</div>
                  <div className="font-medium text-gray-900">
                    {typeof campaign?.deadline === 'string' 
                      ? campaign.deadline 
                      : formatDate(campaign?.deadline)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Product Link</div>
                  {campaign?.productLink ? (
                    <a
                      href={campaign.productLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      View →
                    </a>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              </div>

              {campaign?.hasPhysicalGift && (
                <div className="text-sm mb-4 pt-4 border-t">
                  <span className="text-gray-500">Shipping From:</span>{' '}
                  <span className="font-medium text-gray-900">{campaign?.shippingFromCountry || '—'}</span>
                  {campaign?.shippingNotes && (
                    <div className="text-gray-600 mt-1">{campaign.shippingNotes}</div>
                  )}
                </div>
              )}

              {/* Campaign Bio */}
              {campaign?.bio && (
                <div className="pt-4 border-t">
                  <div className="text-gray-500 text-sm mb-2 font-medium">Campaign Bio</div>
                  <p className="text-gray-800 text-sm whitespace-pre-wrap">{campaign.bio}</p>
                </div>
              )}

              {campaign?.revisionPolicy && (
                <p className="text-xs text-gray-500 italic mt-4 pt-4 border-t">{campaign.revisionPolicy}</p>
              )}
            </section>

            {/* Main grid: Influencer Profile (left) + Status (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Influencer Profile (left, 2 columns) */}
              <section className="lg:col-span-2 space-y-6">
                {/* Header card */}
                <div className="bg-white border rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="h-16 w-16 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center flex-shrink-0 relative">
                      {influencerAvatar && !avatarError ? (
                        <>
                          <img 
                            src={influencerAvatar} 
                            alt={influencerName} 
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              console.warn('❌ Failed to load profile picture from URL:', influencerAvatar);
                              setAvatarError(true);
                              // Hide the broken image
                              if (e.target) {
                                e.target.style.display = 'none';
                              }
                            }}
                            onLoad={() => {
                              console.log('✅ Profile picture loaded successfully from:', influencerAvatar);
                              setAvatarError(false);
                            }}
                          />
                          {/* Fallback initials in case image fails */}
                          {avatarError && (
                            <span className="text-lg font-semibold text-gray-600 absolute inset-0 flex items-center justify-center">
                              {getInitials(influencerName)}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-lg font-semibold text-gray-600">{getInitials(influencerName)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-lg font-semibold text-gray-900">{influencerName}</div>
                      {influencerEmail && (
                        <a
                          href={`mailto:${influencerEmail}`}
                          className="text-xs text-blue-600 hover:underline block"
                        >
                          {influencerEmail}
                        </a>
                      )}
                      {influencerLocation && (
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          {influencerLocation}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Social Handles & Platforms */}
                {(socialLinksWithPlatforms.length > 0 || influencerNiches.length > 0) && (
                  <div className="bg-white border rounded-xl p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Social Handles & Platforms</h3>
                    {socialLinksWithPlatforms.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {socialLinksWithPlatforms.map(({ key, url, platform }) => (
                          <a
                            key={key}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 text-gray-700 transition-colors"
                          >
                            {platform === 'instagram' && '📷'}
                            {platform === 'tiktok' && '🎵'}
                            {platform === 'youtube' && '▶️'}
                            {platform === 'x' && '𝕏'}
                            <span className="font-medium">{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    {influencerNiches.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {influencerNiches.map((niche, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium"
                          >
                            {niche}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Audience & Metrics */}
                {(totalFollowers || engagementRate) && (
                  <div className="bg-white border rounded-xl p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Audience & Metrics</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Stat label="Total Followers" value={totalFollowers ? formatCompact(totalFollowers) : '—'} />
                      <Stat label="Engagement Rate" value={engagementRate ? formatPct(engagementRate) : '—'} />
                      <Stat label="Reach" value="—" />
                    </div>
                  </div>
                )}

                {/* About / Bio */}
                {influencerBio && (
                  <div className="bg-white border rounded-xl p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">About</h3>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{influencerBio}</p>
                  </div>
                )}

                {/* Portfolio / Links */}
                {(influencerProfile?.links?.portfolio || influencerProfile?.links?.mediaKit || influencerProfile?.links?.website) && (
                  <div className="bg-white border rounded-xl p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Links</h3>
                    <ul className="space-y-2 text-sm">
                      {influencerProfile.links.portfolio && (
                        <li>
                          <a
                            href={influencerProfile.links.portfolio}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            Portfolio →
                          </a>
                        </li>
                      )}
                      {influencerProfile.links.mediaKit && (
                        <li>
                          <a
                            href={influencerProfile.links.mediaKit}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            Media Kit →
                          </a>
                        </li>
                      )}
                      {influencerProfile.links.website && (
                        <li>
                          <a
                            href={influencerProfile.links.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            Website →
                          </a>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </section>

              {/* Status (right, 1 column) */}
              <aside className="lg:col-span-1">
                <div className="bg-white border rounded-xl p-5 shadow-sm space-y-4 sticky top-6">
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">Current Status</div>
                    {getStatusBadge(application.status)}
                  </div>

                  {/* Email coordination info box */}
                  <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg">
                    <p className="text-sm text-slate-600">
                      After you accept an influencer, all collaboration details (brief, shipping, deadlines, payment) will be coordinated via email. Use the "Contact Influencer" button below to send next steps directly to them.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Update Status</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      disabled={updating || !application}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {STATUS_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleUpdateStatus}
                      disabled={updating || status === application?.status || !application}
                      className="mt-2 w-full rounded-lg bg-emerald-600 text-white py-2 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {updating ? 'Updating...' : 'Update Status'}
                    </button>
                    {error && (
                      <p className="mt-2 text-sm text-red-600">{error}</p>
                    )}
                  </div>

                  {influencerEmail && (() => {
                    const subject = `InfluenceHub – ${campaignTitle} collaboration`;
                    const body = `Hi ${influencerName},

We're excited to move forward with you for the "${campaignTitle}" campaign on InfluenceHub.

Here are the details we'd like to confirm:
– Deliverables:
– Content deadlines:
– Shipping address (if applicable):
– Payment amount & method:

Please reply to this email so we can finalize everything.

Best,
${brandName}`;
                    const mailtoLink = `mailto:${influencerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    
                    return (
                      <a
                        href={mailtoLink}
                        className="w-full inline-flex justify-center rounded-lg bg-blue-600 text-white py-2 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      >
                        Contact Influencer
                      </a>
                    );
                  })()}
                </div>
              </aside>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Helper component for stats
function Stat({ label, value }) {
  return (
    <div>
      <div className="text-gray-500 text-xs mb-1">{label}</div>
      <div className="font-medium text-gray-900">{value || '—'}</div>
    </div>
  );
}

export default ApplicationDetail;
